"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "../../lib/utils";

// ── Types ──────────────────────────────────────────────────────

interface KanbanContextValue<T = unknown> {
  activeId: UniqueIdentifier | null;
  activeItem: T | null;
  activeColumn: string | null;
  getItemValue: (item: T) => string;
  onItemClick?: (item: T) => void;
  columns: Record<string, T[]>;
}

// ── Context ────────────────────────────────────────────────────

const KanbanContext = createContext<KanbanContextValue | null>(null);

function useKanbanContext() {
  const ctx = useContext(KanbanContext);
  if (!ctx) throw new Error("Kanban components must be used within <Kanban>");
  return ctx;
}

// ── Helpers ────────────────────────────────────────────────────

function findColumnOfItem<T>(
  columns: Record<string, T[]>,
  id: UniqueIdentifier,
  getItemValue: (item: T) => string,
): string | null {
  const strId = String(id);

  // Is it a column droppable id? (prefixed with "column-")
  if (strId.startsWith("column-")) {
    const colId = strId.slice(7);
    if (colId in columns) return colId;
  }

  // Is it a direct column id?
  if (strId in columns) return strId;

  // Search items
  for (const [colId, items] of Object.entries(columns)) {
    if (items.some((item) => getItemValue(item) === strId)) {
      return colId;
    }
  }
  return null;
}

// ── Kanban (root) ──────────────────────────────────────────────

interface KanbanProps<T> {
  value: Record<string, T[]>;
  onValueChange: (value: Record<string, T[]>) => void;
  getItemValue: (item: T) => string;
  onItemClick?: (item: T) => void;
  className?: string;
  children: ReactNode;
}

export function Kanban<T>({
  value,
  onValueChange,
  getItemValue,
  onItemClick,
  className,
  children,
}: KanbanProps<T>) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    for (const items of Object.values(value)) {
      const found = items.find((item) => getItemValue(item) === String(activeId));
      if (found) return found;
    }
    return null;
  }, [activeId, value, getItemValue]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      setActiveId(active.id);
      const col = findColumnOfItem(value, active.id, getItemValue);
      setActiveColumn(col);
    },
    [value, getItemValue],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeCol = findColumnOfItem(value, active.id, getItemValue);
      const overCol = findColumnOfItem(value, over.id, getItemValue);

      if (!activeCol || !overCol || activeCol === overCol) return;

      // Move item to new column
      const activeItems = [...(value[activeCol] ?? [])];
      const overItems = [...(value[overCol] ?? [])];
      const activeIndex = activeItems.findIndex(
        (item) => getItemValue(item) === String(active.id),
      );

      if (activeIndex === -1) return;

      const removed = activeItems.splice(activeIndex, 1);
      const movedItem = removed[0];
      if (!movedItem) return;

      // Find insertion index
      const overIndex = overItems.findIndex(
        (item) => getItemValue(item) === String(over.id),
      );

      if (overIndex === -1) {
        // Dropping on the column itself (empty or at end)
        overItems.push(movedItem);
      } else {
        overItems.splice(overIndex, 0, movedItem);
      }

      onValueChange({
        ...value,
        [activeCol]: activeItems,
        [overCol]: overItems,
      });
    },
    [value, onValueChange, getItemValue],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over) {
        setActiveId(null);
        setActiveColumn(null);
        return;
      }

      const activeCol = findColumnOfItem(value, active.id, getItemValue);
      const overCol = findColumnOfItem(value, over.id, getItemValue);

      if (activeCol && overCol && activeCol === overCol) {
        const items = [...(value[activeCol] ?? [])];
        const oldIndex = items.findIndex(
          (item) => getItemValue(item) === String(active.id),
        );
        const newIndex = items.findIndex(
          (item) => getItemValue(item) === String(over.id),
        );

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          onValueChange({
            ...value,
            [activeCol]: arrayMove(items, oldIndex, newIndex),
          });
        }
      }

      setActiveId(null);
      setActiveColumn(null);
    },
    [value, onValueChange, getItemValue],
  );

  const contextValue: KanbanContextValue<T> = useMemo(
    () => ({
      activeId,
      activeItem,
      activeColumn,
      getItemValue,
      onItemClick,
      columns: value,
    }),
    [activeId, activeItem, activeColumn, getItemValue, onItemClick, value],
  );

  return (
    <KanbanContext.Provider value={contextValue as KanbanContextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className={cn("flex h-full flex-col", className)}>{children}</div>
      </DndContext>
    </KanbanContext.Provider>
  );
}

// ── KanbanBoard ────────────────────────────────────────────────

interface KanbanBoardProps {
  className?: string;
  children: ReactNode;
}

export function KanbanBoard({ className, children }: KanbanBoardProps) {
  return (
    <div
      className={cn(
        "flex flex-1 gap-0 overflow-x-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── KanbanColumn ───────────────────────────────────────────────

interface KanbanColumnProps {
  value: string;
  className?: string;
  children: ReactNode;
}

export function KanbanColumn({ value, className, children }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${value}` });

  return (
    <div
      ref={setNodeRef}
      data-column={value}
      data-over={isOver || undefined}
      className={cn(
        "flex min-w-[260px] flex-1 flex-col",
        "border-r border-[var(--color-border-stroke)] last:border-r-0",
        "transition-colors duration-[var(--duration-fast)]",
        isOver && "bg-[var(--color-bg-hover)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── KanbanColumnHandle ─────────────────────────────────────────

interface KanbanColumnHandleProps {
  className?: string;
  children: ReactNode;
}

export function KanbanColumnHandle({ className, children }: KanbanColumnHandleProps) {
  return (
    <div
      className={cn(
        "shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── KanbanColumnContent ────────────────────────────────────────

interface KanbanColumnContentProps {
  value: string;
  className?: string;
  children: ReactNode;
}

export function KanbanColumnContent({
  value,
  className,
  children,
}: KanbanColumnContentProps) {
  const ctx = useKanbanContext();
  const items = ctx.columns[value] ?? [];
  const itemIds = items.map((item) => ctx.getItemValue(item));

  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <div
        className={cn(
          "flex-1 overflow-y-auto px-[var(--spacing-3)] pb-[var(--spacing-3)]",
          "space-y-[var(--spacing-2)]",
          className,
        )}
      >
        {children}
      </div>
    </SortableContext>
  );
}

// ── KanbanItem ─────────────────────────────────────────────────

interface KanbanItemProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

export function KanbanItem({
  value,
  disabled = false,
  className,
  children,
}: KanbanItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: value,
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-dragging={isDragging || undefined}
      className={cn(
        "touch-none",
        isDragging && "z-50",
        className,
      )}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

// ── KanbanItemHandle ───────────────────────────────────────────

interface KanbanItemHandleProps {
  className?: string;
  children: ReactNode;
}

export function KanbanItemHandle({ className, children }: KanbanItemHandleProps) {
  return (
    <div className={cn("cursor-grab active:cursor-grabbing", className)}>
      {children}
    </div>
  );
}

// ── KanbanOverlay ──────────────────────────────────────────────

interface KanbanOverlayProps {
  className?: string;
  children?: ReactNode | ((activeItem: unknown) => ReactNode);
}

export function KanbanOverlay({ className, children }: KanbanOverlayProps) {
  const { activeId, activeItem } = useKanbanContext();

  if (!activeId) return null;

  const content = typeof children === "function" ? children(activeItem) : children;

  return (
    <DragOverlay>
      <div className={cn("pointer-events-none", className)}>
        {content}
      </div>
    </DragOverlay>
  );
}

// ── useKanbanActive (public hook for overlay content) ──────────

export function useKanbanActive<T = unknown>() {
  const ctx = useKanbanContext();
  return {
    activeId: ctx.activeId,
    activeItem: ctx.activeItem as T | null,
    activeColumn: ctx.activeColumn,
  };
}
